import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { UserPlus, Mail, Lock, User, Phone, Building } from "lucide-react";

const CustomerRegisterPage = () => {
    const { register, user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "", company: "" });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (user) navigate("/account", { replace: true });
    }, [user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
        setSubmitting(true);
        try {
            await register(form);
            navigate("/account", { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Registration failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    return (
        <div className="min-h-[60vh] flex items-center justify-center py-16 px-6" data-testid="customer-register-page">
            <Card className="w-full max-w-md rounded-none border-border">
                <CardContent className="p-8">
                    <div className="text-center mb-8">
                        <div className="w-12 h-12 bg-primary flex items-center justify-center mx-auto mb-4">
                            <UserPlus className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <h1 className="font-heading font-black text-2xl tracking-tight">CREATE ACCOUNT</h1>
                        <p className="text-muted-foreground text-sm mt-1">Track orders and manage your equipment purchases</p>
                    </div>

                    {error && (
                        <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm p-3 mb-6" data-testid="register-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">First Name</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input value={form.first_name} onChange={update("first_name")} className="pl-10 rounded-none" required data-testid="register-first-name" />
                                </div>
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Last Name</label>
                                <Input value={form.last_name} onChange={update("last_name")} className="rounded-none" required data-testid="register-last-name" />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input type="email" value={form.email} onChange={update("email")} className="pl-10 rounded-none" placeholder="your@email.com" required data-testid="register-email" />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input type="password" value={form.password} onChange={update("password")} className="pl-10 rounded-none" placeholder="Min 6 characters" required data-testid="register-password" />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Phone <span className="text-muted-foreground/50">(optional)</span></label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input value={form.phone} onChange={update("phone")} className="pl-10 rounded-none" data-testid="register-phone" />
                            </div>
                        </div>
                        <div>
                            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Company <span className="text-muted-foreground/50">(optional)</span></label>
                            <div className="relative">
                                <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input value={form.company} onChange={update("company")} className="pl-10 rounded-none" data-testid="register-company" />
                            </div>
                        </div>
                        <Button type="submit" disabled={submitting} className="w-full rounded-none uppercase font-bold tracking-wider" data-testid="register-submit-btn">
                            {submitting ? "Creating Account..." : "Create Account"}
                        </Button>
                    </form>

                    <div className="text-center mt-6 text-sm text-muted-foreground">
                        Already have an account?{" "}
                        <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">
                            Sign in
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default CustomerRegisterPage;
